import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { 
  Key, 
  Eye, 
  EyeOff, 
  RotateCcw, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Plus,
  Trash2
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface APIKey {
  id: string;
  name: string;
  provider: string;
  status: 'active' | 'inactive' | 'error';
  lastUsed: string;
  requestsToday: number;
  masked: string;
  isPrimary: boolean;
}

export default function APIKeyManager() {
  const { toast } = useToast();
  
  const [apiKeys, setApiKeys] = useState<APIKey[]>([
    {
      id: '1',
      name: 'Primary OpenAI Key',
      provider: 'OpenAI',
      status: 'active',
      lastUsed: '2 minutes ago',
      requestsToday: 234,
      masked: 'sk-...J8k2',
      isPrimary: true
    },
    {
      id: '2',
      name: 'Backup OpenAI Key',
      provider: 'OpenAI',
      status: 'inactive',
      lastUsed: '3 days ago',
      requestsToday: 0,
      masked: 'sk-...M9n4',
      isPrimary: false
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newKeyForm, setNewKeyForm] = useState({
    name: '',
    provider: 'OpenAI',
    key: ''
  });
  const [autoRotation, setAutoRotation] = useState(false);
  const [rotationDays, setRotationDays] = useState(30);

  const testAPIKey = async (keyId: string) => {
    toast({
      title: "Testing API Key",
      description: "Verifying API key connectivity...",
    });
    
    // Simulate API test
    setTimeout(() => {
      setApiKeys(prev => prev.map(key => 
        key.id === keyId 
          ? { ...key, status: 'active' as const }
          : key
      ));
      
      toast({
        title: "API Key Valid",
        description: "The API key is working correctly.",
      });
    }, 2000);
  };

  const rotateAPIKey = (keyId: string) => {
    toast({
      title: "Key Rotation Initiated",
      description: "A new API key will be generated. The old key will remain active for 24 hours.",
    });
  };

  const toggleKeyStatus = (keyId: string) => {
    setApiKeys(prev => prev.map(key => 
      key.id === keyId 
        ? { 
            ...key, 
            status: key.status === 'active' ? 'inactive' : 'active' 
          }
        : key
    ));
  };

  const setPrimaryKey = (keyId: string) => {
    setApiKeys(prev => prev.map(key => ({
      ...key,
      isPrimary: key.id === keyId
    })));
    
    toast({
      title: "Primary Key Updated",
      description: "The selected key is now the primary API key.",
    });
  };

  const addAPIKey = () => {
    if (!newKeyForm.name || !newKeyForm.key) {
      toast({
        title: "Invalid Input",
        description: "Please provide both name and API key.",
        variant: "destructive"
      });
      return;
    }

    const newKey: APIKey = {
      id: Date.now().toString(),
      name: newKeyForm.name,
      provider: newKeyForm.provider,
      status: 'inactive',
      lastUsed: 'Never',
      requestsToday: 0,
      masked: `${newKeyForm.key.substring(0, 3)}...${newKeyForm.key.slice(-4)}`,
      isPrimary: false
    };

    setApiKeys(prev => [...prev, newKey]);
    setNewKeyForm({ name: '', provider: 'OpenAI', key: '' });
    setShowAddForm(false);
    
    toast({
      title: "API Key Added",
      description: "The new API key has been added successfully.",
    });
  };

  const deleteAPIKey = (keyId: string) => {
    setApiKeys(prev => prev.filter(key => key.id !== keyId));
    toast({
      title: "API Key Deleted",
      description: "The API key has been removed.",
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-success" />;
      case 'inactive':
        return <XCircle className="h-4 w-4 text-muted-foreground" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-destructive" />;
      default:
        return <XCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">API Key Management</h3>
          <p className="text-sm text-muted-foreground">
            Manage and rotate API keys for external services
          </p>
        </div>
        <Button onClick={() => setShowAddForm(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add API Key
        </Button>
      </div>

      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Add New API Key</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Key Name</Label>
                <Input
                  placeholder="e.g., Primary OpenAI Key"
                  value={newKeyForm.name}
                  onChange={(e) => setNewKeyForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Provider</Label>
                <Input
                  value={newKeyForm.provider}
                  onChange={(e) => setNewKeyForm(prev => ({ ...prev, provider: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>API Key</Label>
              <Input
                type="password"
                placeholder="sk-..."
                value={newKeyForm.key}
                onChange={(e) => setNewKeyForm(prev => ({ ...prev, key: e.target.value }))}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={addAPIKey}>Add Key</Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {apiKeys.map((apiKey) => (
          <Card key={apiKey.id} className={apiKey.isPrimary ? "border-primary" : ""}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  {apiKey.name}
                  {apiKey.isPrimary && (
                    <Badge variant="default">Primary</Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {getStatusIcon(apiKey.status)}
                  <Badge variant={apiKey.status === 'active' ? 'default' : 'secondary'}>
                    {apiKey.status}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Provider</Label>
                  <p className="text-sm">{apiKey.provider}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Last Used</Label>
                  <p className="text-sm">{apiKey.lastUsed}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Requests Today</Label>
                  <p className="text-sm">{apiKey.requestsToday}</p>
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Key:</Label>
                  <code className="text-sm bg-muted px-2 py-1 rounded">
                    {apiKey.masked}
                  </code>
                </div>
              </div>

              <Separator className="my-4" />

              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => testAPIKey(apiKey.id)}
                  className="flex items-center gap-1"
                >
                  <CheckCircle className="h-3 w-3" />
                  Test
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => rotateAPIKey(apiKey.id)}
                  className="flex items-center gap-1"
                >
                  <RotateCcw className="h-3 w-3" />
                  Rotate
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleKeyStatus(apiKey.id)}
                  className="flex items-center gap-1"
                >
                  {apiKey.status === 'active' ? (
                    <>
                      <EyeOff className="h-3 w-3" />
                      Deactivate
                    </>
                  ) : (
                    <>
                      <Eye className="h-3 w-3" />
                      Activate
                    </>
                  )}
                </Button>

                {!apiKey.isPrimary && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPrimaryKey(apiKey.id)}
                  >
                    Set Primary
                  </Button>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => deleteAPIKey(apiKey.id)}
                  className="flex items-center gap-1 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Automatic Key Rotation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Enable Auto-Rotation</Label>
              <p className="text-xs text-muted-foreground">
                Automatically rotate API keys on schedule
              </p>
            </div>
            <Switch
              checked={autoRotation}
              onCheckedChange={setAutoRotation}
            />
          </div>

          {autoRotation && (
            <div className="space-y-2">
              <Label>Rotation Interval (days)</Label>
              <Input
                type="number"
                value={rotationDays}
                onChange={(e) => setRotationDays(parseInt(e.target.value))}
                min={7}
                max={365}
                className="w-32"
              />
              <p className="text-xs text-muted-foreground">
                Keys will be rotated every {rotationDays} days
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <strong>Security Note:</strong> API keys are encrypted and stored securely. 
          Keep your keys confidential and rotate them regularly. Never share API keys in public channels.
        </AlertDescription>
      </Alert>
    </div>
  );
}